/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement, useEffect, useRef, useState } from 'react';
import { Form, Formik, FormikProps, getIn } from 'formik';
import FormContainer from '@pages/FormContainer';
import { defaultForm, NominationMetaData, NominationSchema, iNominationFormValues, getSubmissionPayload } from '@services/NominationService';
import { FormNavFooter, FormLoading, Alert, ProgressBar, VFModalSimple, VFSimpleModalWithoutTitle } from '@components/index';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga';
import { FormType } from '@constants';
import { iToolTips, iCountry, iApiError, iFormNavActions, iPartialFormData } from '@interfaces/interfaces';
import { getCountryList, getPartialForm, getTooltips, savePartialForm, submitSavedPartialForm, getBasicInfo, getValidFilesConfig, getGroundsInfo, getObligationOptions } from '@api';

import { navigateToError, getStringIndex, navigateToNextStep, getDateForValidateDebtor, setFormDataValues, setCurrentFormikValues, navToError, navigateToSpecificSteps, bytesToMBSize } from '@utils';

import ErrorContent from '@components/Content/AlertContent/ErrorContent';
import AlreadyHaveASavedFormContent from '@components/Content/AlertContent/AlreadySavedContent';

import VicFinesContext from '../VicFinesFormContext';
import { NominationErrorPage, NominationStep0, NominationStep1, NominationStep10, NominationStep2, NominationStep3, NominationStep4, NominationStep5, NominationStep6, NominationStep7, NominationStep8, NominationStep9, NominationStep11, NominationStep12, NominationStep13, NominationStep14 } from '.';

import IssueIcon from '../../../images/issue.svg';

const {
  REACT_APP_SITECORE_API_ENDPOINT: API_URL,
  REACT_APP_PATH_NOMINATION: PATH_NOMINATION,
} = process.env;

function Nomination(): ReactElement {
  const formikValuesRef = useRef({} as FormikProps<iNominationFormValues>);
  const location = useLocation();
  const navigate = useNavigate();
  const pathElementArray = location.pathname.split('/');
  const stepIndex = getStringIndex(pathElementArray, 'step');
  const stepIndexDiff = stepIndex - pathElementArray.length;
  // example returned [step0,3224242] or [step0]
  const stepArray = pathElementArray.slice(stepIndexDiff);
  // example returned 0
  const stepIndexInPath = getStringIndex(stepArray, 'step');
  // always get the element contain step like step0
  const getStepInPath = stepArray[stepIndexInPath];
  const pathNameLessLastSegment = stepIndex ? location.pathname.split('/').slice(0, stepIndexDiff).join('/') : location.pathname;
  const pathNameUpperCase = location.pathname.toLowerCase();
  const currentStep = pathNameUpperCase === PATH_NOMINATION?.toLowerCase() || pathNameUpperCase === `${PATH_NOMINATION?.toLowerCase()}/` ? 'step0' : `${getStepInPath}`;
  const prevLocation = useRef('');
  const [loading, setLoading] = useState(true);
  const [countryList, setCountryList] = useState<iCountry[]>([]);
  const [tooltips, setTooltips] = useState<iToolTips>({} as iToolTips);
  const [apiError, setApiError] = useState<iApiError>({} as iApiError);
  const [activeError, setActiveError] = useState(false);
  const [isIndividual, setIsIndividual] = useState(false);
  const partialFormAttachmentFields = ['SelectedObligationsStep9', 'SupportingAttachments', 'SignedDeclarationAttachment', 'OtherSupportingAttachments'];
  const arrayFieldsToBeCleared = [
    'ActionsTakenOnFine',
    'obligations',
    'infringementFineArray',
    'enteredObligationsArray',
    'InfringementNums',
    'AgencyNames',
    'AOffence',
    'DAOffence',
  ];
  const dateTypeField = ['DOB', 'DOBSomeoneElse', 'DateSellOrDispose', 'DOBSoldDisposed', 'DOBYourDetails', 'OFFENCE_DATE'];
  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const [countryResults, tooltipResults] = await Promise.all([getCountryList(), getTooltips('fines.NominateForm')]);
        setCountryList(countryResults);
        setTooltips(tooltipResults);
        setLoading(false);
      } catch (error) {
        navigateToError();
      }
    };
    fetchData();
    const isSubmitted = formikValuesRef.current && formikValuesRef.current.values ? formikValuesRef.current.values?.submitted : false;
    const handlePopstate = () => {
      if (window.location.pathname === '/Nominate/Nominate-a-driver/step2') {
        navigate({
          pathname: '/Nominate/Nominate-a-driver/step3',
          search: location.search,
        });
      }
      // Partial Form saved individual user
      if (window.location.pathname === '/Nominate/Nominate-a-driver/step3' && formikValuesRef.current.values.isDebtorTypeIndividual && formikValuesRef.current.values.SaveYesNo === 'true') {
        navigate({
          pathname: '/Nominate/Nominate-a-driver/step4',
          search: location.search,
        });
      }
      // Partial Form saved company user
      if (window.location.pathname === '/Nominate/Nominate-a-driver/step4' && !formikValuesRef.current.values.isDebtorTypeIndividual && formikValuesRef.current.values.SaveYesNo === 'true') {
        navigate({
          pathname: '/Nominate/Nominate-a-driver/step5',
          search: location.search,
        });
      }
      // user should not be able to go back to previous steps after submission
      if (window.location.pathname === '/Nominate/Nominate-a-driver/step13' || isSubmitted) {
        navigate({
          pathname: '/Nominate/Nominate-a-driver/step14',
          search: location.search,
        });
      }
      if ((window.location.pathname === '/Nominate/Nominate-a-driver/error') && !isSubmitted) {
        navigate({
          pathname: '/Nominate/Nominate-a-driver/error',
          search: location.search,
        });
      }
      if ((window.location.pathname === '/Nominate/Nominate-a-driver/step1') && !isSubmitted && formikValuesRef && formikValuesRef.current) {
        formikValuesRef.current.setFieldValue('OBLIGATION_NUMBER', '');
        formikValuesRef.current.setFieldTouched('OBLIGATION_NUMBER');
        formikValuesRef.current.setFieldValue('OFFENCE_DATE', '');
        formikValuesRef.current.setFieldValue('HEARING_DATE', '');
        formikValuesRef.current.setFieldValue('DRIVER_LICENCE', '');
        formikValuesRef.current.setFieldValue('VEHICLE_REGISTRATION', '');
        formikValuesRef.current.setFieldValue('DOB', '');
      }
    };
    window.addEventListener('popstate', handlePopstate);
    return () => {
      window.removeEventListener('popstate', handlePopstate);
    };
  }, []);

  const postObligation = async (formikContext: any): Promise<void> => {
    setLoading(true);
    // remove whitespaces before and after obligation number
    const trimmedObligationNumber = formikContext.values.OBLIGATION_NUMBER.trim();
    formikValuesRef.current.setFieldValue('OBLIGATION_NUMBER', trimmedObligationNumber);
    const payload = {
      obligationNumber: trimmedObligationNumber,
    };
    const url = `${API_URL}sitecore/Nomination/ValidateObligation`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      setLoading(false);
      if (response.status === 400 && responseBody.Error) {
        setApiError(responseBody);
        return;
      }
      if (response.status === 422 && responseBody.Error) {
        setApiError(responseBody);
        return;
      }
      if (response.status === 200) {
        const nextPageUrl = `${pathNameLessLastSegment}${'/step2'}`;
        if (responseBody.obligationNumber) {
          formikValuesRef.current.setFieldValue('OBLIGATION_NUMBER', responseBody.obligationNumber);
        }
        if (responseBody === 'NotInSystem' || responseBody === 'Ineligible') {
          setApiError({
            Error: responseBody,
          });
          return;
        }
        setApiError({
          Error: '',
        });
        navigate(nextPageUrl);
        window.scrollTo(0, 0);
        return;
      }
      navigateToError();
    } catch (error) {
      navigateToError();
    }
  };
  const getTicketInfo = async (obligationNum: string): Promise<void> => {
    try {
      const responseBody = await getBasicInfo(obligationNum, FormType.NOMINATION);
      if (responseBody) {
        formikValuesRef.current.setFieldValue('Status', responseBody.Status);
        formikValuesRef.current.setFieldValue('DebtorType', responseBody.DebtorType);
        if (responseBody.DebtorType === 'Individual') {
          formikValuesRef.current.setFieldValue('isDebtorTypeIndividual', true);
          setIsIndividual(true);
        }
        if (responseBody.DebtorType === 'Individual') {
          navigate(`${pathNameLessLastSegment}/step4`);
        } else {
          navigate(`${pathNameLessLastSegment}/step5`);
        }
        window.scrollTo(0, 0);
      }
    } catch (error) {
      console.error('Failed to get basic info:', error);
    }
  };
  const checkExistingForm = async (formikContext: any): Promise<void> => {
    setLoading(true);
    const payload = {
      ObligationNO: formikContext.values.OBLIGATION_NUMBER,
      formType: FormType.NOMINATION,
    };
    const url = `${API_URL}sitecore/PartialForms/CheckingExistingForm`;

    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      if (response.status === 200) {
        if (responseBody.PartialFormID) formikValuesRef.current.setFieldValue('PartialFormID', responseBody.PartialFormID);
        if (responseBody.DebtorID) formikValuesRef.current.setFieldValue('DebtorID', responseBody.DebtorID, formikContext);
        formikValuesRef.current.setFieldValue('CanSave', responseBody.CanSave);
        if (responseBody.CheckingExisting === 'no' && responseBody.CanSave) {
          setLoading(false);
          navigate(`${pathNameLessLastSegment}/step3`);
          window.scrollTo(0, 0);
          return;
        }
        if (responseBody.PartialFormID) {
          getPartialForm(responseBody.PartialFormID)
            .then((resBody) => {
              if (typeof resBody === 'string') {
                if (resBody === 'Unauthorised') {
                  navigateToError();
                }
              } else {
                if (resBody.Obligations) {
                  if (resBody.Obligations[0].ObligationNO !== formikValuesRef.current.values.OBLIGATION_NUMBER) {
                    formikContext.setFieldValue('alreadySavedForm', true);
                  }
                }
                const formData = JSON.parse(resBody.FormData);
                const keys = Object.keys(resBody);
                keys.forEach((key) => {
                  if (key === 'FormData') {
                    setFormDataValues(formData, formikValuesRef.current.values.FormData, formikValuesRef, 'FormData');
                    setCurrentFormikValues(formData, formikValuesRef.current.values, formikValuesRef);
                  } else if (resBody[key]) {
                    formikContext.setFieldValue(`${key}`, resBody[key]);
                  }
                });
              }
            })
            .catch((e) => console.log(e));
        } else {
          setLoading(false);
        }
        await getTicketInfo(formikContext.values.OBLIGATION_NUMBER);
      }
    } catch (error) {
      navigateToError();
    }
  };
  const getGroundsInfoFileConfig = async (formikContext: any): Promise<void> => {
    if (formikContext.values.OBLIGATION_NUMBER) {
      setLoading(true);
      Promise.all([
        getGroundsInfo(formikContext.values.OBLIGATION_NUMBER, FormType.NOMINATION),
        getValidFilesConfig()
      ])
        .then(([groundsInfo, validFilesConfig]) => {
          if (groundsInfo && groundsInfo?.FineStage) {
            formikValuesRef.current.setFieldValue('FineStage', groundsInfo?.FineStage);
            formikValuesRef.current.setFieldValue('HasBeenNominatedBefore', groundsInfo?.HasBeenNominatedBefore);
            formikValuesRef.current.setFieldValue('OffenceType', groundsInfo?.OffenceType);
            formikValuesRef.current.setFieldValue('IsMarineOffence', groundsInfo?.IsMarineOffence);
            formikValuesRef.current.setFieldValue('IsTsvOffence', groundsInfo?.IsTsvOffence);
            formikValuesRef.current.setFieldValue('IsSeriousOffence', groundsInfo?.IsSeriousOffence);
          }
          if (validFilesConfig) {
            const validFileInfo = validFilesConfig.find((item) => item.FormType === FormType.NOMINATION);
            formikValuesRef.current.setFieldValue('FileUploadSizeConfig', bytesToMBSize(validFileInfo?.FileSize || 0));
            formikValuesRef.current.setFieldValue('FileUploadDescriptionConfig', validFileInfo?.Description);
            formikValuesRef.current.setFieldValue('FileUploadMaxAttachmentsConfig', validFileInfo?.MaxAttachments);
          }
        })
        .catch((e) => {
          console.log(e);
          navigateToError();
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };
  const resetPFormDataValues = (partialFormApi: any, formikObject: any) => {
    if (partialFormApi && formikObject) {
      // clone data format like objectkey: [{},{}]
      if (
        Array.isArray(partialFormApi)
        && partialFormApi[0]
        && typeof partialFormApi[0] === 'object'
        && Array.isArray(formikObject)
        && (formikObject.length !== partialFormApi.length || partialFormApi.length === 0)
      ) {
        partialFormApi.forEach((val) => {
          type dynamicObj = {
            [key: string]: string;
          };
          const temp = {} as dynamicObj;
          Object.keys(val).forEach((key) => {
            temp[key] = val[key];
          });
          formikObject.push(temp);
        });
      }
      // handle scenario if formikObject is empty array , it's for ObjectKey:[] or string[]
      if (
        Array.isArray(partialFormApi)
        && partialFormApi[0]
        && typeof partialFormApi[0] === 'string'
        && Array.isArray(formikObject)
        && (formikObject.length !== partialFormApi.length || partialFormApi.length === 0)
      ) {
        partialFormApi.forEach((val) => {
          formikObject.push(val);
        });
      }
      if (partialFormApi && formikObject) {
        Object.keys(partialFormApi).forEach((key) => {
          if (key in formikObject) {
            if (typeof partialFormApi[key] === 'object' && !(partialFormApi[key] instanceof Date)) {
              if (partialFormAttachmentFields.includes(key) || arrayFieldsToBeCleared.includes(key)) {
                // eslint-disable-next-line no-param-reassign
                formikObject[key] = [];
              }
              resetPFormDataValues(partialFormApi[key], formikObject[key]);
            } else {
              // eslint-disable-next-line no-param-reassign
              formikObject[key] = partialFormApi[key];
            }
          }
        });
      }
    }
  };
  /* const checkIfAnyChanges = (partialFormApi: any, formikObject: any): boolean => {
    if (!(formikObject && partialFormApi)) {
      return false;
    }
    let isChanged = false;
    Object.keys(formikObject).forEach((key) => {
      if (key in partialFormApi) {
        if (typeof partialFormApi[key] === 'object' && partialFormApi[key] && !(partialFormApi[key] instanceof Date)) {
          if (Array.isArray(partialFormApi[key])) {
            if (partialFormApi[key].length !== formikObject[key].length) {
              isChanged = true;
            }
          }
          checkIfAnyChanges(partialFormApi[key], formikObject[key]);
        } else if (dateTypeField.includes(key) && partialFormApi[key] && partialFormApi[key]?.getTime() !== formikObject[key]?.getTime?.()) {
          isChanged = true;
        } else if (partialFormApi[key] && partialFormApi[key] !== formikObject[key]) {
          isChanged = true;
        }
      }
    });
    console.log(isChanged);
    return isChanged;
  }; */
  const step2NextPage = () => {
    navigateToNextStep(navigate, location, 'next');
  };
  const getObOptions = async (formikContext: any) => {
      getObligationOptions(formikContext.values.OBLIGATION_NUMBER, FormType.NOMINATION)
        .then((responseBody) => {
          if (responseBody && responseBody?.Authorised) {
            formikContext.setFieldValue('OBLIGATION_NUMBER', responseBody?.Authorised.Number);
            formikContext.setFieldValue('InfringementNoticeNumber', responseBody?.Authorised.InfringementNumber);
        }
      }).catch((e) => console.log(e));
  };

  const validateDebtor = async (formikContext: any): Promise<void> => {
    setLoading(true);
    // Backend api is checking date time for some reasons, time need to be 14:00:00
    const getOffenceDate = formikContext.values.OFFENCE_DATE && getDateForValidateDebtor(formikContext.values.OFFENCE_DATE.toLocaleDateString('en-AU'));
    const getHearingDate = formikContext.values.HEARING_DATE && getDateForValidateDebtor(formikContext.values.HEARING_DATE.toLocaleDateString('en-AU'));
    const getDOBDate = formikContext.values.DOB && getDateForValidateDebtor(formikContext.values.DOB.toLocaleDateString('en-AU'));
    // const dateObj = getTheDate.split('/');
    // const specificedDateString = `${dateObj[2]}-${dateObj[1]}-${dateObj[0]}`;
    const payload = {
      dateOfBirth: getDOBDate,
      hearingDate: getHearingDate,
      licenceNumber: formikContext.values.DRIVER_LICENCE,
      obligationNumber: formikContext.values.OBLIGATION_NUMBER,
      offenceDate: getOffenceDate,
      registrationNumber: formikContext.values.VEHICLE_REGISTRATION,
    };
    const url = `${API_URL}sitecore/Nomination/ValidateDebtor`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      setLoading(false);
      if (response.status === 400 && responseBody.Error) {
        setApiError(responseBody);
        return;
      }
      if (response.status === 200) {
        setApiError({
          Error: '',
        });
        if (responseBody === '/step4' || responseBody === '/step5') {
          formikValuesRef.current.setFieldValue('DOBYourDetails', getIn(formikContext.values, 'DOB'));

          getObOptions(formikContext);
          getGroundsInfoFileConfig(formikContext);
          checkExistingForm(formikContext);
        }
      }
    } catch (error) {
      navigateToError();
    }
  };

  const createPartialForm = async (formikContext: any): Promise<void> => {
    if (formikContext.values.SaveYesNo === 'false' || getIn(formikContext.values, 'PartialFormID')) {
      if (formikContext.values.isDebtorTypeIndividual) {
        navigateToNextStep(navigate, location, 'next');
      } else {
        navigate(`${pathNameLessLastSegment}/step5`);
      }
      return;
    }
    setLoading(true);
    const { values } = formikContext;
    resetPFormDataValues(values, formikContext.values.FormData);
    const payload = {
      Attachments: '',
      CreateDate: '',
      DebtorID: `${formikContext.values.DebtorID}`,
      EMRemeinderSent: false,
      Email: formikContext.values.saveEmail,
      ExpiryDate: '',
      FormData: JSON.stringify({ a: '1', ...formikContext.values.FormData }),
      FormTypeID: 3,
      ID: 0,
      IsSubscribedEM: !!formikContext.values.saveEmail,
      IsSubscribedSMS: !!formikContext.values.saveMobile,
      Mobile: formikContext.values.saveMobile,
      Obligations: [
        {
          ID: 0,
          ObligationNO: formikContext.values.OBLIGATION_NUMBER,
          IsPrimary: true,
        },
      ],
      SMSRemeinderSent: false,
    };
    const url = `${API_URL}sitecore/PartialForms/CreatePartialForm`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      setLoading(false);
      if (response.status === 200) {
        if (responseBody.ID) {
          formikValuesRef.current.setFieldValue('PartialFormID', responseBody.ID);
          formikValuesRef.current.setFieldValue('Obligations', responseBody.Obligations);
        }
        if (formikContext.values.isDebtorTypeIndividual) {
          navigateToNextStep(navigate, location, 'next');
        } else {
          navigate(`${pathNameLessLastSegment}/step5`);
        }
        return;
      }
      navigateToError();
    } catch (error) {
      console.log(error);
    }
  };
  const updatePartialFormData = async (formikContext: any): Promise<boolean> => {
    const { values } = formikContext;
    resetPFormDataValues(values, formikContext.values.FormData);
    const partialformPayload: iPartialFormData = {
      ID: formikContext.values.PartialFormID,
      Obligations: formikContext.values.Obligations,
      Attachments: [],
      FormData: JSON.stringify(formikContext.values.FormData), // {a: '1'},formikContext.values.FormData
      DebtorID: formikContext.values.DebtorID,
      FormTypeID: 3,
      Email: formikContext.values.saveEmail,
      Mobile: formikContext.values.saveMobile,
      IsSubscribedEM: !!formikContext.values.saveEmail,
      IsSubscribedSMS: !!formikContext.values.saveMobile,
      EMRemeinderSent: formikContext.values.EMRemeinderSent,
      SMSRemeinderSent: formikContext.values.SMSRemeinderSent,
      CreateDate: formikContext.values.CreateDate,
      ExpiryDate: formikContext.values.ExpiryDate,
    };
    try {
      const response = await savePartialForm(partialformPayload);
      return response;
    } catch (error) {
      console.log(error);
      return false;
    }
  };
  const step4NextPage = (formikContext: any) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    navigateToNextStep(navigate, location, 'next');
  };
  /* const fetchRegObligationNums = (selectedObligations: iFVSCheckboxAll[], authorisedObligation: string) => [
    authorisedObligation,
    ...selectedObligations.filter((e: iFVSCheckboxAll) => e.checked).map(({ number }: { number: string }) => number),
  ]; */
  const step5NextPage = async (formikContext: any) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    setLoading(true);
    // const payload = fetchRegObligationNums(formikContext.values.SelectedObligationsStep5, formikContext.values.AuthorisedObligation.Number);
    // await formikContext.setFieldValue('SelectedObligations', payload);
    const selectedGround = formikContext.values.SelectedGroundInfo;
    const selectedOption = formikContext.values.selectTheOption;
    switch (true) {
      case selectedOption === 'SelectedGroundInfo0':
        navigateToSpecificSteps(navigate, location, '6');
        break;
      case selectedOption === 'SelectedGroundInfo1':
        navigateToSpecificSteps(navigate, location, '7');
        break;
      case selectedOption === 'SelectedGroundInfo2':
        navigateToSpecificSteps(navigate, location, '8');
        break;
      case selectedOption === 'SelectedGroundInfo3':
        navigateToSpecificSteps(navigate, location, '9');
        break;
      case selectedOption === 'SelectedGroundInfo4':
        navigateToSpecificSteps(navigate, location, '10');
        break;
      default:
        navigateToNextStep(navigate, location, 'next');
        break;
    }
    setLoading(false);
  };
  const step6NextPage = (
    formikContext: any
  ) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    navigateToSpecificSteps(navigate, location, '11');
  };
  const step7NextPage = (
    formikContext: any
  ) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    navigateToSpecificSteps(navigate, location, '11');
  };
  const step8NextPage = (
    formikContext: any
  ) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    navigateToSpecificSteps(navigate, location, '11');
  };
  const step9NextPage = (
    formikContext: any
  ) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    navigateToSpecificSteps(navigate, location, '11');
  };
  const step10NextPage = (
    formikContext: any
  ) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    navigateToNextStep(navigate, location, 'next');
  };
  const step11NextPage = (

    formikContext: any
  ) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    sessionStorage.setItem('isFromConfirm', '');
    navigateToNextStep(navigate, location, 'next');
  };

  const formNavActions: iFormNavActions = {
    step1: {
      next: postObligation,
    },
    step2: {
      next: validateDebtor,
    },
    step3: {
      next: createPartialForm,
    },
    step4: {
      next: step4NextPage,
    },
    step5: {
      next: step5NextPage,
    },
    step6: {
      next: step6NextPage,
    },
    step7: {
      next: step7NextPage,
    },
    step8: {
      next: step8NextPage,
    },
    step9: {
      next: step9NextPage,
    },
    step10: {
      next: step10NextPage,
    },
    step11: {
      next: step11NextPage,
    }
  };

  const clearApiError = () => {
    setApiError({
      Error: '',
    });
  };

  const displayErrorsCount = (errorCount: number, touchedFields: number): void => {
    if (touchedFields === 0) {
      setActiveError(true);
    } else if (errorCount > 0) {
      setActiveError(true);
    } else {
      setActiveError(false);
    }
  };

  const createPartialFormForSumbit = async (formValues: iNominationFormValues): Promise<string> => {
    formikValuesRef.current.setFieldValue('SaveYesNo', 'true');
    resetPFormDataValues(formikValuesRef.current.values, formValues.FormData);
    const payload = {
      Attachments: '',
      CreateDate: '',
      DebtorID: `${formValues.DebtorID}`,
      EMRemeinderSent: false,
      Email: '',
      ExpiryDate: '',
      FormData: JSON.stringify({ ...formValues.FormData, SaveYesNo: 'true' }),
      FormTypeID: 3,
      ID: 0,
      IsSubscribedEM: false,
      IsSubscribedSMS: false,
      Mobile: '',
      Obligations: [
        {
          ID: 0,
          ObligationNO: formValues.OBLIGATION_NUMBER,
          IsPrimary: true,
        },
      ],
      SMSRemeinderSent: false,
    };
    const url = `${API_URL}sitecore/PartialForms/CreatePartialForm`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      if (responseBody.status === 200) {
        return 'true';
      }
      return responseBody;
    } catch (error) {
      return `${error}`;
    }
  };

  const getNominationEndpoint = (formValues: iNominationFormValues): string => {
    if (formValues.IsMarineOffence && formValues.IsSeriousOffence) {
      if (formValues.isDebtorTypeIndividual) {
        return 'SubmitBACTSVIndividual';
      }
      return 'SubmitBACTSVCompany';
    }
    if (formValues.IsMarineOffence && !formValues.IsSeriousOffence) {
      if (formValues.isDebtorTypeIndividual) {
        return 'SubmitTSVIndividual';
      }
      return 'SubmitTSVCompany';
    }
    if (!formValues.IsMarineOffence && formValues.IsSeriousOffence) {
      if (formValues.isDebtorTypeIndividual) {
        return 'SubmitESIIndividual';
      }
      return 'SubmitESICompany';
    }

    if (!formValues.IsMarineOffence && !formValues.IsSeriousOffence) {
      if (formValues.isDebtorTypeIndividual) {
        return 'SubmitStatementIndividual';
      }
      return 'SubmitStatementCompany';
    }
    return '';
  };

  const submitForm = async (formValues: iNominationFormValues): Promise<boolean> => {
    const submissionNumber = 'SubmissionNumber';
    const formSubmissionValues = getSubmissionPayload(formValues, submissionNumber, getNominationEndpoint(formValues));
    const submitPayload = {
      data: JSON.stringify(formSubmissionValues),
      obligationNo: formValues.OBLIGATION_NUMBER
    };

    const urlSubmission = `${API_URL}sitecore/Nomination/${getNominationEndpoint(formValues)}`;
    let submitResponse = false;
    setLoading(true);
    submitResponse = await fetch(urlSubmission, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submitPayload),
    })
      .then((formSubResponse) => {
        if (formSubResponse.status === 200) {
          if (formValues.SaveYesNo === 'true') {
            // having partial form, close it
            submitSavedPartialForm(formValues.PartialFormID);
          }
          formikValuesRef.current.setFieldValue('submitted', true);
          ReactGA.event({
            category: 'Nomination Form',
            action: 'Submit',
            label: 'Submit Nomination Form',
          });
          // navigate to submission success
          return formSubResponse.json();
        }
        formikValuesRef.current.setFieldValue('submissionError', formSubResponse.statusText);
        if (formValues.SaveYesNo === 'false') {
          createPartialFormForSumbit(formValues);
        }
        navToError(navigate, location);
        return false;
      }).then((response) => {
        if (response) {
          if (response.receiptNo) {
            formikValuesRef.current.setFieldValue('submissionNo', response.receiptNo); // get submission number from API response
            return true;
          }
          navToError(navigate, location);
          return false;
        }
        return false;
      }).catch((error) => {
        formikValuesRef.current.setFieldValue('submissionError', error);
        if (formValues.SaveYesNo === 'false') {
          createPartialFormForSumbit(formValues);
        }
        console.log(error);
        navToError(navigate, location);
        return false;
      })
      .finally(() => {
        setLoading(false);
      });
    if (!submitResponse) return false;
    return true;
  };
  return (
    <Formik
      initialValues={defaultForm()}
      innerRef={formikValuesRef}
      validationSchema={NominationSchema}
      onSubmit={(values: iNominationFormValues) => {
        submitForm(values);
      }}>
      {({ values }) => (
        <VicFinesContext.Provider value={{ countryList, setCountryList, tooltips, setTooltips, apiError, clearApiError }}>
          <FormContainer title="Nomination">
            {loading && <FormLoading />}
            {!loading && (
              <Form>
                {!location.pathname.includes('error') && <ProgressBar formMetaData={NominationMetaData} />}
                {activeError && <Alert title="Sorry there is a problem" icon={IssueIcon} color="red" message={<ErrorContent />} theme="error" />}
                {values.alreadySavedForm && <VFSimpleModalWithoutTitle show shouldCloseOnOverlayClick={false} content={<AlreadyHaveASavedFormContent />} />}
                <Routes>
                  <Route path="/step0/:ob" element={<NominationStep0 formikRef={formikValuesRef} />} />
                  <Route path="/step1" element={<NominationStep1 />} />
                  <Route path="/step2" element={<NominationStep2 />} />
                  <Route path="/step3" element={<NominationStep3 />} />
                  <Route path="/step4" element={<NominationStep4 />} />
                  <Route path="/step5" element={<NominationStep5 />} />
                  <Route path="/step6" element={<NominationStep6 />} />
                  <Route path="/step7" element={<NominationStep7 />} />
                  <Route path="/step8" element={<NominationStep8 />} />
                  <Route path="/step9" element={<NominationStep9 />} />
                  <Route path="/step10" element={<NominationStep10 />} />
                  <Route path="/step11" element={<NominationStep11 />} />
                  <Route path="/step12" element={<NominationStep12 />} />
                  <Route path="/step13" element={<NominationStep13 />} />
                  <Route path="/step14" element={<NominationStep14 />} />
                  <Route path="/error" element={<NominationErrorPage />} />
                  <Route path="/" index element={<NominationStep0 formikRef={formikValuesRef} />} />
                </Routes>

                {!(location.pathname === `${PATH_NOMINATION}/step14` || location.pathname === `${PATH_NOMINATION}/error`) && (
                  <FormNavFooter
                    totalPages={14}
                    formNavActions={formNavActions}
                    submitPage={13}
                    submit={async () => submitForm(values)}
                    tooltips={tooltips}
                    displayErrorsCount={displayErrorsCount}
                    pageValidationKeys={NominationMetaData.pagesSegments[currentStep].validationKeys(values)}
                    formType={FormType.NOMINATION}
                    firstPageNo={0}
                    disableThePage={4}
                  />
                )}
              </Form>
            )}
          </FormContainer>
        </VicFinesContext.Provider>
      )}
    </Formik>
  );
}

export default Nomination;
